import { useState } from 'react'
import { Check, Library } from 'lucide-react'
import { Button, Chip, Select, Textarea, toast } from '@/components/ui'
import { cn } from '@/lib/utils/cn'
import { useUpdateItem } from '../api/queries'
import type { AnswerChoice, LibraryAction, QuestionnaireItem } from '../api/types'
import { ACTION_LABEL, CHOICE_LABEL, STATUS_META, formatDate } from '../utils/meta'

export interface ItemCardProps {
  questionnaireId: string
  item: QuestionnaireItem
  editable: boolean
  onPickFromLibrary: (item: QuestionnaireItem) => void
}

const CHOICES: AnswerChoice[] = ['yes', 'no', 'na']

/** One question: its answer, where the answer came from, and what approval
 *  will do with it in the library. */
export function ItemCard({ questionnaireId, item, editable, onPickFromLibrary }: ItemCardProps) {
  const [choice, setChoice] = useState<AnswerChoice | null>(item.answer_choice)
  const [text, setText] = useState(item.answer_text ?? '')
  const update = useUpdateItem()

  // The parent keys the card on the saved answer, so a save or re-match
  // starts it afresh from the server copy.
  const dirty = choice !== item.answer_choice || text !== (item.answer_text ?? '')

  const status = STATUS_META[item.status]
  const entry = item.library_entry
  const stale = item.flags.includes('stale_answer')

  function save(extra: { accept?: boolean } = {}) {
    const body: Parameters<typeof update.mutate>[0] = { questionnaireId, itemId: item.id, ...extra }
    if (choice !== item.answer_choice) body.answer_choice = choice ?? ''
    if (text !== (item.answer_text ?? '')) body.answer_text = text
    update.mutate(body, {
      onError: (err) =>
        toast.danger(`Couldn't save${err instanceof Error ? `: ${err.message}` : ''}`),
    })
  }

  function setAction(action: LibraryAction | '') {
    update.mutate(
      { questionnaireId, itemId: item.id, library_action: action },
      {
        onError: (err) =>
          toast.danger(`Couldn't save${err instanceof Error ? `: ${err.message}` : ''}`),
      },
    )
  }

  const actions: LibraryAction[] = entry ? ['variant', 'update', 'new', 'skip'] : ['new', 'skip']

  return (
    <article
      className={cn(
        'rounded-md border bg-surface p-4',
        item.needs_review ? 'border-warning-border' : 'border-subtle',
        item.status === 'unanswered' && 'border-danger-border',
      )}
      aria-label={`Question ${item.ref ?? item.seq}`}
    >
      <header className="mb-2 flex items-start gap-3">
        <span className="min-w-[2.5rem] font-mono text-sm text-tertiary">
          {item.ref ?? item.seq}
        </span>
        <p className="flex-1 font-medium">{item.question_text}</p>
        <Chip variant={status.variant} dot title={status.hint}>
          {status.label}
        </Chip>
      </header>

      <div className="ml-[3.25rem] flex flex-col gap-2">
        {stale ? (
          <p className="text-sm text-warning-text">
            This library answer was due for review on {formatDate(entry?.review_due)} — check it is
            still true.
          </p>
        ) : null}

        {item.answer_type === 'yes_no_na' ? (
          <div className="flex gap-1" role="radiogroup" aria-label="Answer">
            {CHOICES.map((c) => (
              <Button
                key={c}
                size="sm"
                variant={choice === c ? 'primary' : 'secondary'}
                role="radio"
                aria-checked={choice === c}
                disabled={!editable}
                onClick={() => setChoice(choice === c ? null : c)}
              >
                {CHOICE_LABEL[c]}
              </Button>
            ))}
          </div>
        ) : null}

        {editable ? (
          <Textarea
            aria-label="Answer"
            rows={
              item.answer_type === 'field'
                ? 1
                : Math.min(8, Math.max(2, Math.ceil(text.length / 90)))
            }
            value={text}
            placeholder={item.answer_type === 'yes_no_na' ? 'Explanation' : 'Answer'}
            onChange={(e) => setText(e.target.value)}
          />
        ) : (
          <p className="whitespace-pre-wrap text-sm">
            {item.answer_text || <span className="italic text-tertiary">No answer</span>}
          </p>
        )}

        {entry ? (
          <div className="text-xs text-secondary">
            <span>
              Library: “{entry.canonical_question}”
              {item.match_fit ? ` · ${item.match_fit} match` : ''}
              {item.confidence != null ? ` · ${Math.round(item.confidence * 100)}%` : ''}
            </span>
            {item.match_note ? (
              <span className="block text-tertiary">{item.match_note}</span>
            ) : null}
            {!entry.same_answer ? (
              <span className="mt-1 block rounded-xs bg-neutral-bg p-2 text-secondary">
                Library answer:{' '}
                {entry.answer_choice ? `${CHOICE_LABEL[entry.answer_choice]} — ` : ''}
                {entry.answer_text || '—'}
              </span>
            ) : null}
          </div>
        ) : null}
        {item.citation ? <p className="text-xs text-secondary">Source: {item.citation}</p> : null}

        {editable ? (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            {dirty ? (
              <Button size="sm" variant="primary" loading={update.isPending} onClick={() => save()}>
                Save answer
              </Button>
            ) : item.status !== 'accepted' &&
              (item.answer_text || item.answer_choice || item.needs_review) ? (
              <Button
                size="sm"
                variant="secondary"
                loading={update.isPending}
                onClick={() => save({ accept: true })}
              >
                <Check size={14} />{' '}
                {item.answer_text || item.answer_choice ? 'Accept' : 'Leave blank'}
              </Button>
            ) : null}
            <Button size="sm" variant="ghost" onClick={() => onPickFromLibrary(item)}>
              <Library size={14} /> Use a library answer
            </Button>
            <label className="ml-auto flex items-center gap-2 text-xs text-secondary">
              On approval
              <Select
                aria-label="Library action on approval"
                value={item.library_action ?? ''}
                onChange={(e) => setAction(e.target.value as LibraryAction | '')}
              >
                <option value="">{ACTION_LABEL[item.default_library_action]} (default)</option>
                {actions.map((a) => (
                  <option key={a} value={a}>
                    {ACTION_LABEL[a]}
                  </option>
                ))}
              </Select>
            </label>
          </div>
        ) : null}
      </div>
    </article>
  )
}
